/**
 * The rail ids a user can hide, in rail order, and the rule that keeps at
 * least one of them on screen. Icon-free on purpose: the extensions store
 * imports this, and it must not drag the UI (lucide, React) into its graph.
 */
export const RAIL_TOOL_IDS = Object.freeze([
  'collections',
  'history',
  'git',
  'db-manager.activity',
  'terminal.activity',
  'ftp.activity',
  'mail-catcher.activity',
  'projects.activity',
  'task-manager.activity',
  'documents.activity',
  'vault.activity',
  'graph.activity',
  'chat.activity',
  'calendar.activity',
]);

/**
 * Tools that only make sense next to another one (owner, 20.10.2026): History
 * is the API Client's request history, so turning the API Client off turns
 * History off too, and turning it back on brings History back. History can
 * still be hidden on its own while the API Client is on.
 */
export const RAIL_FOLLOWERS = Object.freeze({ collections: Object.freeze(['history']) });

/** The tool `id` follows, or null. */
export function railLeaderOf(id) {
  return Object.keys(RAIL_FOLLOWERS).find((leader) => RAIL_FOLLOWERS[leader].includes(id)) || null;
}

/** `hidden` plus the followers of every hidden leader (an older saved list may lack them). */
export function withFollowers(hidden) {
  const out = new Set(Array.isArray(hidden) ? hidden : []);
  for (const leader of Object.keys(RAIL_FOLLOWERS)) {
    if (out.has(leader)) RAIL_FOLLOWERS[leader].forEach((f) => out.add(f));
  }
  return out;
}

/** `hidden` after switching `id`: a leader takes its followers with it. */
export function toggleRailId(hidden, id) {
  const current = Array.isArray(hidden) ? hidden : [];
  const followers = RAIL_FOLLOWERS[id] || [];
  if (current.includes(id)) return current.filter((x) => x !== id && !followers.includes(x));
  return [...current, id, ...followers.filter((f) => !current.includes(f))];
}

/**
 * A clean `railHidden` list: known ids only, no duplicates, a hidden leader's
 * followers hidden too, and never every one of `available` (the tools this
 * build can show). If the input would hide them all, the first available tool
 * is kept visible.
 */
export function normalizeRailHidden(ids, available = RAIL_TOOL_IDS) {
  const known = new Set(RAIL_TOOL_IDS);
  const out = [];
  for (const id of Array.isArray(ids) ? ids : []) {
    if (typeof id === 'string' && known.has(id) && !out.includes(id)) out.push(id);
  }
  for (const leader of Object.keys(RAIL_FOLLOWERS)) {
    if (!out.includes(leader)) continue;
    for (const f of RAIL_FOLLOWERS[leader]) if (!out.includes(f)) out.push(f);
  }
  const visible = available.filter((id) => !out.includes(id));
  if (available.length > 0 && visible.length === 0) {
    return out.filter((id) => id !== available[0]);
  }
  return out;
}

/** True when hiding `id` (and its followers) would leave none of `available` visible. */
export function isLastVisible(hidden, id, available = RAIL_TOOL_IDS) {
  const set = new Set(hidden || []);
  if (set.has(id) || !available.includes(id)) return false;
  const after = new Set(toggleRailId(hidden, id));
  return available.filter((x) => !after.has(x)).length === 0;
}
